import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";

import AppShell from "./components/AppShell";
import ProtectedRoute from "./components/ProtectedRoute";
import PurchaseSelectionPage from "./routes/PurchaseSelectionPage";
import AngebotReviewPage from "./routes/AngebotReviewPage";
import AngeboteListPage from "./routes/AngeboteListPage";
import ArticleDetailLayout from "./routes/articleDetail/ArticleDetailLayout";
import OverviewTab from "./routes/articleDetail/tabs/OverviewTab";
import ArticleIdentityReviewPage from "./routes/ArticleIdentityReviewPage";
import ArticlesOverviewPage from "./routes/ArticlesOverviewPage";
import BelegDetailRoute, { BelegDetailRedirect, BelegeCreditRedirect } from "./routes/BelegDetailRoute";
import BelegePage from "./routes/BelegePage";
import CartPage from "./routes/CartPage";
import CockpitPage from "./routes/CockpitPage";
import CostCenterDetailPage from "./routes/CostCenterDetailPage";
import CostCentersPage from "./routes/CostCentersPage";
import ImportsPage from "./routes/ImportsPage";
import ImportQualityReviewPage from "./routes/ImportQualityReviewPage";
import LegalPage from "./routes/LegalPage";
import LoginPage from "./routes/LoginPage";
import MembersPage from "./routes/MembersPage";
import OrderDetailPage from "./routes/OrderDetailPage";
import OrderConfirmationsPage from "./routes/OrderConfirmationsPage";
import PriceVarianceReportPage from "./routes/PriceVarianceReportPage";
import SourceCoveragePage from "./routes/SourceCoveragePage";
import SettingsPage from "./routes/SettingsPage";
import { OrdersHubLayout, OrdersListPage, PriceRequestsListPage, UploadsHubLayout } from "./routes/HubLayouts";
import { costCenterListHref, projectCostCenterHref } from "./lib/costCenterDisplay";
import { DATENSCHUTZ, IMPRESSUM } from "./legal/content.generated";

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/impressum" element={<LegalPage doc={IMPRESSUM} />} />
      <Route path="/datenschutz" element={<LegalPage doc={DATENSCHUTZ} />} />
      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<CockpitPage />} />
        <Route path="/articles" element={<ArticlesOverviewPage />} />
        <Route
          path="/articles/identity-review"
          element={<ArticleIdentityReviewPage />}
        />
        <Route path="/articles/:articleKey" element={<ArticleDetailLayout />}>
          <Route index element={<OverviewTab />} />
          <Route path="preise" element={<LegacyArticleTabRedirect />} />
          <Route path="bestellungen" element={<LegacyArticleTabRedirect />} />
          <Route path="lieferanten" element={<LegacyArticleTabRedirect />} />
          <Route path="nachweise" element={<LegacyArticleTabRedirect />} />
          <Route path="stamm" element={<LegacyArticleTabRedirect />} />
        </Route>
        <Route
          path="/projects"
          element={<Navigate to={costCenterListHref("project")} replace />}
        />
        <Route path="/projects/:projectKey" element={<ProjectRedirect />} />
        <Route path="/warenkorb" element={<CartPage />} />
        <Route path="/belege" element={<BelegePage mode="archive" />} />
        <Route path="/rechnungspruefung" element={<BelegePage mode="review" />} />
        {/* Gutschriften sind eine Belegart, keine eigene Seite (#539). */}
        <Route path="/belege/gutschriften" element={<BelegeCreditRedirect />} />
        <Route path="/belege/:belegId" element={<BelegDetailRoute />} />
        <Route path="/preisspiegel" element={<AngeboteListPage mode="picker" />} />
        <Route path="/angebote/vergleichen" element={<AngebotReviewPage />} />
        <Route path="/angebote/auswahl/:selectionId" element={<PurchaseSelectionPage />} />
        <Route path="/angebote/:angebotId" element={<BelegDetailRedirect param="angebotId" />} />
        {/* Bestellungen in der Kopfzeile: vier frühere Seiten als Reiter (#630). */}
        <Route element={<OrdersHubLayout />}>
          <Route path="/bestellungen" element={<OrdersListPage />} />
          <Route path="/preisanfrage" element={<PriceRequestsListPage />} />
          <Route path="/angebote" element={<AngeboteListPage />} />
          <Route path="/auftragsbestaetigungen" element={<OrderConfirmationsPage />} />
        </Route>
        <Route
          path="/auftragsbestaetigungen/:orderConfirmationId"
          element={<BelegDetailRedirect param="orderConfirmationId" />}
        />
        <Route path="/orders" element={<Navigate to="/bestellungen" replace />} />
        <Route path="/orders/:orderKey" element={<OrderDetailPage />} />
        <Route path="/cost-centers" element={<CostCentersPage />} />
        <Route
          path="/cost-centers/detail/:costCenterKey"
          element={<CostCenterDetailPage />}
        />
        <Route path="/reports/variance" element={<PriceVarianceReportPage />} />
        {/* Artikelvergleich entfällt (#630). */}
        <Route path="/reports/product-opportunities" element={<Navigate to="/articles" replace />} />
        {/* „Belege hochladen“ im Konto-Menü: Hochladen, Verlauf, Datenqualität, Dateibestand (#630). */}
        <Route element={<UploadsHubLayout />}>
          <Route path="/imports" element={<ImportsPage section="upload" />} />
          <Route path="/imports/verlauf" element={<ImportsPage section="history" />} />
          <Route path="/imports/review" element={<ImportQualityReviewPage />} />
          <Route path="/coverage" element={<SourceCoveragePage />} />
        </Route>
        <Route path="/members" element={<MembersPage />} />
        <Route path="/einstellungen" element={<SettingsPage />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

function NotFound() {
  return (
    <main className="mx-auto max-w-2xl p-8">
      <h1 className="text-2xl font-semibold">Seite nicht gefunden</h1>
      <p className="mt-2 text-slate-600">Die angeforderte URL existiert nicht.</p>
    </main>
  );
}

function ProjectRedirect() {
  const { projectKey } = useParams<{ projectKey: string }>();
  return (
    <Navigate
      to={projectCostCenterHref(projectKey) ?? costCenterListHref("project")}
      replace
    />
  );
}

/** Die früheren Artikel-Reiter führen auf die eine Artikelseite; Zeitraum und Rückweg bleiben (#630). */
function LegacyArticleTabRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: "..", search: location.search }} relative="path" replace state={location.state} />;
}
